import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef, useCallback } from 'react';
import { Loader2, MicOff } from 'lucide-react';
import DailyIframe from '@daily-co/daily-js';
import { isLemonSliceEnabled, createLemonSliceRoom } from '../services/lemonsliceService';

const ringColorFor = (isSpeaking, isListening) => {
    if (isSpeaking) return '#22d3ee';
    if (isListening) return '#a3e635';
    return '#e879f9';
};

/**
 * LemonSliceAvatar — círculo de video con el avatar de Lemon Slice haciendo
 * lipsync sobre el TTS que ya genera tu backend. Pipeline:
 *
 *   1. Backend crea sala Daily.co self-managed (POST /api/lemonslice/room).
 *   2. Componente se une "headless" (sin UI prebuilt de Daily) al room.
 *   3. Renderiza la pista de video remota (el avatar) en un <video>.
 *   4. En pushAudio(blob): reproduce localmente para que el usuario lo oiga
 *      Y publica el audio como track WebRTC al room para que Lemon Slice
 *      lo procese y genere el video con lip-sync.
 *
 * Expone vía ref:
 *   - pushAudio(blob): reproduce + envía al avatar para lipsync.
 *   - attachAudioElement(audioEl): camino alterno desde un <audio>.
 *   - clearBuffer(): no-op (Lemon Slice no expone interrupt API por ahora).
 */
const LemonSliceAvatar = forwardRef(function LemonSliceAvatar(
    { isSpeaking = false, isListening = false, isThinking = false, size = 360, agentId = null, onReady = null },
    ref,
) {
    const enabled = isLemonSliceEnabled();
    const [status, setStatus] = useState(enabled ? 'connecting' : 'disabled');

    // Aviso al padre cuando el avatar está listo para hablar. Si Lemon Slice
    // está deshabilitado o cayó en error no hay nada que esperar — disparamos
    // onReady para no bloquear la UI indefinidamente.
    useEffect(() => {
        if (!enabled || status === 'ready' || status === 'error') {
            onReady?.();
        }
    }, [enabled, status, onReady]);
    const [errorMsg, setErrorMsg] = useState('');
    const videoRef = useRef(null);
    const remoteAudioRef = useRef(null);
    const callRef = useRef(null);
    const cleanupTimerRef = useRef(null);
    // Pipeline Web Audio persistente: AudioContext + MediaStreamDestination
    // alimenta un único track que se publica al room. Cada blob TTS se
    // decodifica a un BufferSource que se conecta tanto al destino del
    // stream (→ Lemon Slice) como a los altavoces (→ usuario).
    const audioCtxRef = useRef(null);
    const streamDestRef = useRef(null);   // alimenta el track 'stream' (Lemon Slice)
    const micDestRef = useRef(null);       // alimenta el track 'audio' (default mic)
    const customTrackRef = useRef(null);   // track 'stream'
    const micTrackRef = useRef(null);      // track 'audio' (passed as audioSource)
    const trackPublishedRef = useRef(false);
    const sessionIdRef = useRef(null);
    const speakingRef = useRef(false);
    const responseFinishTimerRef = useRef(null);
    const botSessionIdRef = useRef(null);
    // Stream de mic silencioso (track.enabled = false) que mantenemos abierto
    // para que Chrome conserve la sesión de audio en modo "communication" /
    // full-duplex. Sin esto, en Windows + headsets BT/USB la salida del audio
    // remoto de LS suena más opaca hasta que el STT abre el mic.
    const silentMicStreamRef = useRef(null);
    const ringColor = ringColorFor(isSpeaking, isListening);

    const attachRemoteTracks = useCallback((participant) => {
        if (!participant || participant.local) return;
        const videoTrack = participant.tracks?.video?.persistentTrack;
        const audioTrack = participant.tracks?.audio?.persistentTrack;
        if (videoTrack && videoRef.current && videoRef.current.srcObject !== videoTrack) {
            console.log('[LemonSlice] attaching remote video from', participant.user_name);
            videoRef.current.srcObject = new MediaStream([videoTrack]);
            videoRef.current.play().catch(() => {});
        }
        // Reproducimos el audio remoto del avatar — viene perfectamente
        // sincronizado con la animación de los labios. NO reproducimos el
        // TTS local en paralelo (sería eco / desincronizado).
        if (audioTrack && remoteAudioRef.current && remoteAudioRef.current.srcObject !== audioTrack) {
            console.log('[LemonSlice] attaching remote audio from', participant.user_name);
            remoteAudioRef.current.srcObject = new MediaStream([audioTrack]);
            remoteAudioRef.current.play().catch(() => {});
        }
    }, []);

    /** Crea AudioContext + 2 destinos (mic + stream) + tracks. */
    const ensureAudioPipeline = useCallback(() => {
        if (audioCtxRef.current && customTrackRef.current && micTrackRef.current) return;
        const Ctx = window.AudioContext || window.webkitAudioContext;
        const ctx = new Ctx();
        const streamDest = ctx.createMediaStreamDestination();
        const micDest = ctx.createMediaStreamDestination();
        audioCtxRef.current = ctx;
        streamDestRef.current = streamDest;
        micDestRef.current = micDest;
        customTrackRef.current = streamDest.stream.getAudioTracks()[0];
        micTrackRef.current = micDest.stream.getAudioTracks()[0];

        // Oscilador silencioso conectado a AMBOS destinos: mantiene los
        // tracks "vivos" ante WebRTC y hace que el participante quede como
        // audio: true (necesario para que LS subscribe al track 'stream').
        const silentOsc = ctx.createOscillator();
        const silentGain = ctx.createGain();
        silentGain.gain.value = 0.0001;
        silentOsc.connect(silentGain);
        silentGain.connect(streamDest);
        silentGain.connect(micDest);
        silentOsc.start();

        console.log('[LemonSlice] pipeline: stream-track',
            customTrackRef.current?.id, '/ mic-track', micTrackRef.current?.id);
    }, []);

    /** Abre un getUserMedia silencioso y lo deja vivo. Sirve solo de
     *  side-effect: fuerza a Chrome a operar la pestaña en modo full-duplex,
     *  lo que mejora la nitidez del audio remoto entrante (LS) en Windows
     *  con headsets. Si el usuario rechaza el permiso, no pasa nada — el
     *  resto del pipeline LS sigue funcionando con audioSource sintético. */
    const acquireSilentMic = useCallback(async () => {
        if (silentMicStreamRef.current) return;
        if (!navigator.mediaDevices?.getUserMedia) return;
        try {
            const ms = await navigator.mediaDevices.getUserMedia({ audio: true });
            ms.getAudioTracks().forEach((t) => { t.enabled = false; });
            silentMicStreamRef.current = ms;
            console.log('[LemonSlice] 🎙️ silent mic acquired (full-duplex on)');
        } catch (err) {
            console.warn('[LemonSlice] silent mic acquire failed:', err?.name || err);
        }
    }, []);

    /** Confirma estado del track local tras unirse. */
    const verifyLocalAudio = useCallback(() => {
        const call = callRef.current;
        if (!call) return;
        try {
            const localP = call.participants()?.local;
            console.log('[LemonSlice] 👤 local participant:', {
                session_id: localP?.session_id,
                audio: localP?.audio,
                tracks_audio: localP?.tracks?.audio,
                tracks_keys: Object.keys(localP?.tracks || {}),
            });
        } catch (_) {}
    }, []);

    const connect = useCallback(async (cancelRef) => {
        if (!enabled) return;
        setStatus('connecting');
        setErrorMsg('');
        // Pide el mic silencioso en paralelo con la creación de la sala.
        // No bloqueamos el connect: si tarda o falla, el flujo sigue.
        acquireSilentMic();
        try {
            const { room_url, session_id } = await createLemonSliceRoom({ agentId: agentId || undefined });
            if (cancelRef?.cancelled) return;
            if (!room_url) throw new Error('No room_url returned');
            sessionIdRef.current = session_id;
            console.log('[LemonSlice] session_id:', session_id);

            // Crea pipeline ANTES de unirse para tener los tracks listos
            ensureAudioPipeline();
            const customTrack = customTrackRef.current;
            const micTrack = micTrackRef.current;
            customTrack.enabled = true;
            micTrack.enabled = true;

            // Limpia cualquier instancia previa (StrictMode dev double-mount)
            const existing = DailyIframe.getCallInstance && DailyIframe.getCallInstance();
            if (existing) { try { await existing.destroy(); } catch (_) {} }

            // Pasamos micTrack como audioSource → Daily marca audio: true.
            // Sin esto, Lemon Slice ve a nuestro participante como mudo y
            // no se subscribe al custom track 'stream'.
            const call = DailyIframe.createCallObject({
                audioSource: micTrack,
                videoSource: false,
                subscribeToTracksAutomatically: true,
                userName: 'Pipecat',
            });
            callRef.current = call;

            call.on('joined-meeting', async () => {
                if (cancelRef?.cancelled) return;
                console.log('[LemonSlice] ✅✅✅ joined-meeting EVENTO');
                Object.values(call.participants()).forEach(attachRemoteTracks);
                setStatus('ready');
                console.log('[LemonSlice] startCustomTrack typeof:', typeof call.startCustomTrack);
                console.log('[LemonSlice] track antes de publicar:', {
                    id: customTrack.id, enabled: customTrack.enabled,
                    muted: customTrack.muted, readyState: customTrack.readyState,
                });
                try {
                    if (typeof call.startCustomTrack === 'function') {
                        console.log('[LemonSlice] llamando startCustomTrack...');
                        const r = await call.startCustomTrack({
                            track: customTrack,
                            trackName: 'stream',
                            mode: 'speech',
                        });
                        trackPublishedRef.current = true;
                        console.log('[LemonSlice] ✅ startCustomTrack stream OK, result:', r);
                    } else {
                        console.warn('[LemonSlice] ⚠️ startCustomTrack NO existe en el callObject');
                    }
                } catch (e) {
                    console.error('[LemonSlice] ❌ startCustomTrack fallo:', e?.message, e);
                }
                setTimeout(verifyLocalAudio, 600);
                setTimeout(() => {
                    try {
                        const ps = call.participants();
                        console.log('[LemonSlice] 🔍 todos los participantes:', JSON.stringify(Object.keys(ps)));
                        Object.entries(ps).forEach(([k, p]) => {
                            console.log(`  - ${k}: user_name=${p.user_name}, audio=${p.audio}, tracks=`, Object.keys(p.tracks || {}));
                        });
                    } catch (_) {}
                }, 1500);
            });
            call.on('participant-joined', (e) => {
                console.log('[LemonSlice] participant-joined:', e?.participant?.user_name, e?.participant?.tracks);
                attachRemoteTracks(e?.participant);
            });
            call.on('participant-updated', (e) => attachRemoteTracks(e?.participant));
            call.on('track-started', (e) => {
                console.log('[LemonSlice] track-started:', e?.track?.kind, 'from', e?.participant?.user_name);
                attachRemoteTracks(e?.participant);
            });
            call.on('app-message', (e) => {
                console.log('[LemonSlice] 📩 app-message:', e?.fromId, e?.data);
                if (e?.data?.type === 'bot_ready' && e?.fromId) {
                    botSessionIdRef.current = e.fromId;
                    console.log('[LemonSlice] 🎯 botSessionId capturado:', e.fromId);
                }
            });
            call.on('error', (err) => {
                console.error('[LemonSlice] error', err);
                setErrorMsg(err?.errorMsg || 'connection error');
                setStatus('error');
            });

            await call.join({ url: room_url });
        } catch (err) {
            console.error('[LemonSlice] connect failed:', err);
            setErrorMsg(err.message || 'Connection failed');
            setStatus('error');
        }
    }, [enabled, agentId, attachRemoteTracks, ensureAudioPipeline, verifyLocalAudio, acquireSilentMic]);

    useEffect(() => {
        if (!enabled) return undefined;
        // Cancela cleanup pendiente del unmount-remount de StrictMode
        if (cleanupTimerRef.current) {
            clearTimeout(cleanupTimerRef.current);
            cleanupTimerRef.current = null;
        }
        const cancelRef = { cancelled: false };
        // 50ms de gracia para evitar el doble-mount de StrictMode en dev.
        const startTimer = setTimeout(() => {
            if (!cancelRef.cancelled && !callRef.current) connect(cancelRef);
        }, 50);
        return () => {
            cancelRef.cancelled = true;
            clearTimeout(startTimer);
            // Defer cleanup 200ms para que el remount inmediato lo cancele.
            cleanupTimerRef.current = setTimeout(() => {
                const c = callRef.current;
                const ctx = audioCtxRef.current;
                const silentMs = silentMicStreamRef.current;
                callRef.current = null;
                customTrackRef.current = null;
                micTrackRef.current = null;
                streamDestRef.current = null;
                micDestRef.current = null;
                audioCtxRef.current = null;
                silentMicStreamRef.current = null;
                trackPublishedRef.current = false;
                cleanupTimerRef.current = null;
                if (c) {
                    c.leave().then(() => c.destroy()).catch(() => {});
                }
                if (ctx) {
                    try { ctx.close(); } catch (_) {}
                }
                if (silentMs) {
                    try { silentMs.getTracks().forEach((t) => t.stop()); } catch (_) {}
                }
            }, 200);
        };
    }, [enabled, connect]);

    /** Decodifica blob y lo manda al destino del stream (→ Lemon Slice).
     *  Si toSpeakers=true, también sale por los altavoces locales.
     *  Devuelve la duración del audio en segundos (0 si falló). */
    const decodeAndStream = useCallback(async (audioBlob, { toSpeakers }) => {
        if (!enabled || !audioBlob) return 0;
        ensureAudioPipeline();

        const ctx = audioCtxRef.current;
        const streamDest = streamDestRef.current;
        const micDest = micDestRef.current;
        if (!ctx || !streamDest || !micDest) {
            console.warn('[LemonSlice] pipeline no disponible');
            return 0;
        }
        if (ctx.state === 'suspended') {
            try { await ctx.resume(); console.log('[LemonSlice] ctx resumed'); } catch (_) {}
        }
        const t = customTrackRef.current;
        if (t && !t.enabled) { t.enabled = true; console.log('[LemonSlice] re-enabled track'); }

        try {
            const arrayBuffer = await audioBlob.arrayBuffer();
            const audioBuffer = await ctx.decodeAudioData(arrayBuffer.slice(0));
            const source = ctx.createBufferSource();
            source.buffer = audioBuffer;
            if (toSpeakers) source.connect(ctx.destination);
            source.connect(streamDest);  // → custom track 'stream' (LS)
            source.connect(micDest);     // → track 'audio' (mic, hace audio:true)

            // Aviso a Lemon Slice que empezamos a hablar (Pipecat hace esto
            // vía sendAppMessage; sin esta señal, el avatar puede quedarse en
            // idle e ignorar el track de audio).
            const call = callRef.current;
            const sid = sessionIdRef.current;
            const botId = botSessionIdRef.current;
            if (call && sid && !speakingRef.current) {
                speakingRef.current = true;
                try {
                    // Mandamos el mensaje con AMBOS keys (`event` y `type`)
                    // por si el avatar usa el segundo, como en bot_ready.
                    const msg = {
                        event: 'response_started',
                        type: 'response_started',
                        topic: 'lemonslice',
                        session_id: sid,
                    };
                    call.sendAppMessage(msg, botId || '*');
                    console.log('[LemonSlice] 📨 response_started enviado a', botId || '*');
                } catch (e) { console.warn('[LemonSlice] sendAppMessage response_started fallo:', e); }
            }

            // Cancelamos finish previo si llega un nuevo chunk antes de tiempo
            if (responseFinishTimerRef.current) {
                clearTimeout(responseFinishTimerRef.current);
                responseFinishTimerRef.current = null;
            }

            source.start();
            console.log('[LemonSlice] 🔊 stream', audioBuffer.duration.toFixed(2), 's', toSpeakers ? '(+speakers)' : '(track only)');

            // Programa response_finished para 500ms después del fin del audio.
            // Si llega otro chunk antes, se cancela y se pospone.
            const finishMs = Math.ceil(audioBuffer.duration * 1000) + 500;
            responseFinishTimerRef.current = setTimeout(() => {
                responseFinishTimerRef.current = null;
                if (callRef.current && sessionIdRef.current && speakingRef.current) {
                    speakingRef.current = false;
                    try {
                        const target = botSessionIdRef.current || '*';
                        callRef.current.sendAppMessage(
                            {
                                event: 'response_finished',
                                type: 'response_finished',
                                topic: 'lemonslice',
                                session_id: sessionIdRef.current,
                            },
                            target,
                        );
                        console.log('[LemonSlice] 📨 response_finished enviado a', target);
                    } catch (e) { console.warn('[LemonSlice] sendAppMessage response_finished fallo:', e); }
                }
            }, finishMs);

            // 1s después: ¿realmente están saliendo bytes al room?
            setTimeout(async () => {
                try {
                    const call = callRef.current;
                    const pc = call?._network?.peerConnection || call?._pc;
                    if (!pc?.getStats) {
                        console.log('[LemonSlice] 📊 no peerConnection accesible para stats');
                        return;
                    }
                    const stats = await pc.getStats();
                    let outAudio = null;
                    stats.forEach((s) => { if (s.type === 'outbound-rtp' && s.kind === 'audio') outAudio = s; });
                    console.log('[LemonSlice] 📊 outbound audio:', outAudio
                        ? { bytesSent: outAudio.bytesSent, packetsSent: outAudio.packetsSent }
                        : 'no outbound-rtp/audio');
                } catch (_) {}
            }, 1000);

            return audioBuffer.duration;
        } catch (err) {
            console.error('[LemonSlice] decodeAndStream failed:', err);
            return 0;
        }
    }, [enabled, ensureAudioPipeline]);

    /** Camino 1: App.jsx no reproduce nada. Mandamos al avatar y dejamos
     *  que el audio remoto del avatar (sincronizado con el lip-sync) se
     *  oiga por el <audio> remoto. NO reproducimos local para evitar
     *  desincronía/eco. */
    const pushAudio = useCallback(async (audioBlob) => {
        return await decodeAndStream(audioBlob, { toSpeakers: false });
    }, [decodeAndStream]);

    /** Camino 2: App.jsx crea un <audio> y le hace .play() — lo silenciamos
     *  para que solo se oiga el audio que vuelve del avatar. */
    const attachAudioElement = useCallback(async (audioEl) => {
        if (!enabled || !audioEl?.src) return;
        try {
            audioEl.muted = true;
            audioEl.volume = 0;
        } catch (_) {}
        try {
            const res = await fetch(audioEl.src);
            const blob = await res.blob();
            await decodeAndStream(blob, { toSpeakers: false });
        } catch (err) {
            console.warn('[LemonSlice] attachAudioElement failed:', err);
        }
    }, [enabled, decodeAndStream]);

    const clearBuffer = useCallback(() => {
        // Web Audio sources son one-shot; no hay buffer que limpiar.
        // Para interrumpir habría que trackear sources activos y stop()-earlos.
    }, []);

    useImperativeHandle(ref, () => ({ pushAudio, attachAudioElement, clearBuffer }), [pushAudio, attachAudioElement, clearBuffer]);

    // ── Render ──
    return (
        <div
            className="relative flex items-center justify-center select-none"
            style={{ width: size, height: size }}
            data-tour="avatar"
        >
            {(isSpeaking || isListening) && (
                <>
                    <div className="absolute rounded-full animate-ping" style={{ inset: -8, background: `${ringColor}1A`, animationDuration: '1.8s' }} />
                    <div className="absolute rounded-full animate-ping" style={{ inset: -18, background: `${ringColor}0D`, animationDuration: '2.4s', animationDelay: '0.5s' }} />
                </>
            )}

            <div
                className="absolute inset-0 rounded-full overflow-hidden"
                style={{
                    background: 'linear-gradient(150deg, #181830 0%, #0f0f1f 100%)',
                    border: `1.5px solid ${ringColor}80`,
                    boxShadow: `inset 0 0 24px rgba(34, 211, 238, 0.08), 0 0 24px ${ringColor}33`,
                }}
            >
                {enabled ? (
                    <>
                        <video
                            ref={videoRef}
                            autoPlay
                            playsInline
                            muted
                            className="absolute inset-0 w-full h-full object-cover"
                            style={{
                                filter: 'contrast(1.05) saturate(1.1)',
                                objectPosition: 'center top',
                            }}
                        />
                        {/* Audio remoto (si el avatar emitiera audio propio) */}
                        <audio ref={remoteAudioRef} autoPlay style={{ display: 'none' }} />
                        <div
                            className="absolute inset-0 pointer-events-none"
                            style={{
                                background: `radial-gradient(ellipse 60% 40% at 50% 100%, ${ringColor}22 0%, transparent 60%)`,
                            }}
                        />
                        {status !== 'ready' && (
                            <div className="absolute inset-0 flex flex-col items-center justify-center bg-bg-900/60 backdrop-blur-sm">
                                {status === 'error' ? (
                                    <>
                                        <MicOff className="w-6 h-6 text-red-400 mb-2" />
                                        <span className="text-[9px] font-mono tracking-widest uppercase text-red-400 px-3 text-center">
                                            {errorMsg || 'Avatar offline'}
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        <Loader2 className="w-7 h-7 text-neon-cyan animate-spin mb-2" />
                                        <span className="text-[9px] font-mono tracking-widest uppercase text-neon-cyan/80">
                                            {status === 'connecting' ? 'connecting…' : status}
                                        </span>
                                    </>
                                )}
                            </div>
                        )}
                    </>
                ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <div
                            className="w-16 h-16 rounded-full mb-3"
                            style={{
                                background: `radial-gradient(circle at 30% 30%, ${ringColor}aa, ${ringColor}22)`,
                                boxShadow: `0 0 24px ${ringColor}55, inset 0 0 12px ${ringColor}33`,
                                animation: 'glow-breathe 3s ease-in-out infinite',
                            }}
                        />
                        <span className="text-[9px] font-mono tracking-[0.3em] uppercase" style={{ color: ringColor, opacity: 0.7 }}>
                            video layer
                        </span>
                        <span className="text-[8px] font-mono tracking-widest uppercase text-slate-500 mt-1">
                            lemon slice
                        </span>
                    </div>
                )}
            </div>

            <div
                className="absolute bottom-2 right-2 w-5 h-5 rounded-full"
                style={{
                    background: isThinking ? '#fbbf24' : isSpeaking ? '#22d3ee' : isListening ? '#a3e635' : '#475569',
                    boxShadow: isThinking || isSpeaking || isListening
                        ? `0 0 10px currentColor`
                        : 'none',
                    border: '2px solid #0a0a14',
                }}
            />
        </div>
    );
});

export default LemonSliceAvatar;